import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import TextField from '@material-ui/core/TextField';
import InputAdornment from '@material-ui/core/InputAdornment';
import {
  Box, Dialog, Divider, Grid, Toolbar, Typography, useMediaQuery, useTheme,
} from '@material-ui/core';
import Slide from '@material-ui/core/Slide';
import { TransitionGroup } from 'react-transition-group';
import { ArrowBack, Close, Search as SearchIcon } from '@material-ui/icons';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import StarIcon from '@material-ui/icons/Star';
import StarBorderIcon from '@material-ui/icons/StarBorder';
import { v4 as uuidv4 } from 'uuid';

import { alertRoutine } from '../../../../redux/Alerts/routines';
import { postParamRoutine, putParamRoutine } from '../../../../redux/Params/routines';
import { makeParamsSelector } from '../../../../redux/Params/selectors';
import { collectionKeysEqualityFn } from '../../../../utils/equality';
import { CAISSES_REFERENCE } from '../../../../data/caissesReference';

export const CAISSE_PARAM_NAME = 'caisse-list';

// Champs éditables d'une caisse (ordre d'affichage dans le formulaire).
const FIELDS = [
  { key: 'libelle', label: 'Libellé', full: true },
  { key: 'regime', label: 'Régime', full: true },
  { key: 'codeRegime', label: 'Code régime' },
  { key: 'caisseGestionnaire', label: 'Caisse gestionnaire' },
  { key: 'centreGestionnaire', label: 'Centre gestionnaire' },
  { key: 'organismeDestinataire', label: 'Organisme destinataire' },
  { key: 'codeCentreInformatique', label: 'Code centre informatique' },
  { key: 'adresse1', label: 'Adresse 1', full: true },
  { key: 'adresse2', label: 'Adresse 2', full: true },
  { key: 'codePostal', label: 'Code postal' },
  { key: 'commune', label: 'Commune' },
  { key: 'cedex', label: 'Cédex' },
  { key: 'telephone', label: 'Téléphone' },
  { key: 'fax', label: 'Fax' },
];

const EMPTY = FIELDS.reduce((acc, f) => ({ ...acc, [f.key]: '' }), {});

const caisseLabel = (c) => {
  const base = (c?.libelle || 'Sans nom').trim();
  const reg = (c?.regime || '').trim();
  return reg ? `${base} — ${reg}` : base;
};

function CaissesDialog({ open, handleClose, handleReject }) {
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down('sm'));
  const leftWidth = isSmDown ? '100%' : 360;
  const dispatch = useDispatch();

  const paramsSelector = useMemo(makeParamsSelector, []);
  const params = useSelector(
    (state) => paramsSelector(state, CAISSE_PARAM_NAME),
    collectionKeysEqualityFn(['id', 'updatedAt']),
  );
  const param = params[0];
  const list = useMemo(() => param?.value || [], [param]);

  const [elements, setElements] = useState(list);
  const [mode, setMode] = useState('idle'); // idle | create | edit
  const [selectedId, setSelectedId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [search, setSearch] = useState('');
  const [importInfo, setImportInfo] = useState('');

  useEffect(() => {
    setElements(list);
  }, [list.map((l) => l.id).join(), param?.updatedAt]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveParam = (values) => {
    if (param) {
      dispatch(putParamRoutine({ id: param.id, values: { value: values } }));
    } else {
      dispatch(postParamRoutine({ name: CAISSE_PARAM_NAME, value: values }));
    }
    setElements(values);
  };

  const showList = !isSmDown || mode === 'idle';
  const showForm = !isSmDown || mode === 'create' || mode === 'edit';

  const activeElements = useMemo(
    () => elements.filter((e) => !e.archive),
    [elements],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const base = !q
      ? activeElements
      : activeElements.filter((c) => (
        `${c.libelle} ${c.regime} ${c.commune} ${c.codePostal} ${c.caisseGestionnaire} ${c.centreGestionnaire}`
          .toLowerCase()
          .includes(q)
      ));
    // Favoris en tête, puis ordre alphabétique du libellé.
    return [...base].sort((a, b) => {
      const fa = a.favorite ? 0 : 1;
      const fb = b.favorite ? 0 : 1;
      if (fa !== fb) return fa - fb;
      return String(a.libelle || '').localeCompare(String(b.libelle || ''));
    });
  }, [activeElements, search]);

  const CAP = 200;
  const shown = filtered.slice(0, CAP);

  const loadIntoForm = (c) => {
    setForm(FIELDS.reduce((acc, f) => ({ ...acc, [f.key]: c?.[f.key] ?? '' }), { id: c?.id }));
  };

  const startCreate = () => {
    setMode('create');
    setSelectedId(null);
    setForm({ ...EMPTY });
    setImportInfo('');
  };

  const startEdit = (id) => {
    const it = elements.find((x) => x.id === id);
    if (!it) return;
    setMode('edit');
    setSelectedId(id);
    loadIntoForm(it);
    setImportInfo('');
  };

  const resetToIdle = () => {
    setMode('idle');
    setSelectedId(null);
    setForm({ ...EMPTY });
  };

  const setField = (key) => (e) => {
    const v = e?.target?.value ?? '';
    setForm((prev) => ({ ...prev, [key]: v }));
  };

  const handleSave = () => {
    const libelle = String(form.libelle || '').trim();
    if (!libelle) return;
    if (mode === 'create') {
      const created = { ...form, libelle, id: uuidv4(), archive: false };
      saveParam([...elements, created]);
      setSelectedId(created.id);
      setMode('edit');
    } else if (mode === 'edit' && selectedId) {
      const index = elements.findIndex((x) => x.id === selectedId);
      if (index === -1) return;
      const updated = { ...elements[index], ...form, libelle };
      saveParam([...elements.slice(0, index), updated, ...elements.slice(index + 1)]);
    }
  };

  const handleArchive = (id) => (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    const index = elements.findIndex((x) => x.id === id);
    if (index === -1) return;
    const updated = { ...elements[index], archive: !elements[index].archive };
    saveParam([...elements.slice(0, index), updated, ...elements.slice(index + 1)]);
    if (id === selectedId) resetToIdle();
  };

  const handleToggleFavorite = (id) => (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    const index = elements.findIndex((x) => x.id === id);
    if (index === -1) return;
    const updated = { ...elements[index], favorite: !elements[index].favorite };
    saveParam([...elements.slice(0, index), updated, ...elements.slice(index + 1)]);
  };

  // Importe les caisses du référentiel embarqué (CSV) qui ne sont pas déjà présentes (par id).
  const handleImport = () => {
    const existing = new Set(elements.map((e) => e.id));
    const toAdd = CAISSES_REFERENCE.filter((c) => !existing.has(c.id)).map((c) => ({ ...c }));
    if (!toAdd.length) {
      setImportInfo('Référentiel déjà à jour : aucune caisse à importer.');
      return;
    }
    saveParam([...elements, ...toAdd]);
    setImportInfo(`${toAdd.length} caisse(s) importée(s) depuis le référentiel.`);
  };

  const getTitle = () => {
    if (mode === 'create') return 'Nouvelle caisse';
    if (mode === 'edit') return 'Modifier la caisse';
    return 'Caisses';
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      fullScreen={isSmDown}
      maxWidth="lg"
      PaperProps={{ style: { height: isSmDown ? '100vh' : '80vh', display: 'flex' } }}
      TransitionProps={{ onExited: handleReject }}
    >
      <Toolbar style={{ borderBottom: '1px solid rgba(0,0,0,0.12)', paddingLeft: 8, paddingRight: 8 }}>
        {isSmDown && !showList && (
          <IconButton onClick={resetToIdle}><ArrowBack /></IconButton>
        )}
        {!isSmDown && (
          <IconButton aria-label="Fermer" onClick={handleClose}><Close /></IconButton>
        )}
        <Typography variant="h6" style={{ flex: 1, marginLeft: 8 }}>{getTitle()}</Typography>
        {showList && (
          <>
            <Button onClick={handleImport} style={{ marginRight: 8 }}>Importer le référentiel</Button>
            <Button variant="outlined" onClick={startCreate}>Nouvelle</Button>
          </>
        )}
      </Toolbar>

      <Box style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        <TransitionGroup component={null}>
          {showList && (
            <Slide key="list" direction="right" in mountOnEnter unmountOnExit>
              <Box style={{ width: leftWidth, borderRight: '1px solid rgba(0,0,0,0.12)', display: 'flex', flexDirection: 'column' }}>
                <Box p={1}>
                  <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    placeholder="Rechercher une caisse…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
                  />
                  {!!importInfo && (
                    <Typography variant="caption" color="textSecondary">{importInfo}</Typography>
                  )}
                </Box>
                <Divider />
                <Box style={{ flex: 1, overflow: 'auto' }}>
                  {activeElements.length === 0 ? (
                    <Box p={2}>
                      <Typography variant="body2" color="textSecondary">
                        Aucune caisse. Cliquez sur « Importer le référentiel » pour charger la liste nationale.
                      </Typography>
                    </Box>
                  ) : (
                    <List component="nav" dense>
                      {shown.map((c) => (
                        <ListItem
                          key={c.id}
                          button
                          selected={selectedId === c.id}
                          onClick={() => startEdit(c.id)}
                        >
                          <ListItemText
                            primary={c.libelle || 'Sans nom'}
                            secondary={[c.regime, c.commune].filter(Boolean).join(' · ')}
                          />
                          <IconButton
                            size="small"
                            onClick={handleToggleFavorite(c.id)}
                            title={c.favorite ? 'Retirer des favoris' : 'Ajouter aux favoris'}
                          >
                            {c.favorite
                              ? <StarIcon fontSize="small" style={{ color: '#f5b301' }} />
                              : <StarBorderIcon fontSize="small" />}
                          </IconButton>
                          <IconButton size="small" edge="end" onClick={handleArchive(c.id)} title="Archiver">
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </ListItem>
                      ))}
                      {filtered.length > CAP && (
                        <Box p={2}>
                          <Typography variant="caption" color="textSecondary">
                            {filtered.length} résultats — affinez la recherche pour voir les suivants.
                          </Typography>
                        </Box>
                      )}
                    </List>
                  )}
                </Box>
              </Box>
            </Slide>
          )}

          {showForm && (
            <Slide key="form" direction="left" in mountOnEnter unmountOnExit>
              <Box style={{ flex: 1, padding: 16, overflow: 'auto' }}>
                {mode === 'idle' ? (
                  <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                    <Typography variant="body2" color="textSecondary">
                      Sélectionnez une caisse à gauche, ou créez-en une nouvelle.
                    </Typography>
                  </Box>
                ) : (
                  <>
                    <Grid container spacing={2}>
                      {FIELDS.map((f) => (
                        <Grid item xs={12} md={f.full ? 12 : 6} key={f.key}>
                          <TextField
                            fullWidth
                            size="small"
                            variant="outlined"
                            label={f.label}
                            value={form[f.key] ?? ''}
                            onChange={setField(f.key)}
                            error={f.key === 'libelle' && !String(form.libelle || '').trim()}
                            helperText={f.key === 'libelle' && !String(form.libelle || '').trim() ? 'Obligatoire' : ''}
                          />
                        </Grid>
                      ))}
                    </Grid>
                    <Box mt={2} display="flex" justifyContent="flex-end" style={{ gap: 8 }}>
                      {mode === 'edit' && (
                        <Button
                          onClick={handleArchive(selectedId)}
                          style={{ color: theme.palette.error.main, marginRight: 'auto' }}
                          startIcon={<DeleteOutlineIcon />}
                        >
                          Archiver
                        </Button>
                      )}
                      <Button onClick={resetToIdle}>Annuler</Button>
                      <Button
                        color="primary"
                        variant="contained"
                        onClick={handleSave}
                        disabled={!String(form.libelle || '').trim()}
                      >
                        Enregistrer
                      </Button>
                    </Box>
                  </>
                )}
              </Box>
            </Slide>
          )}
        </TransitionGroup>
      </Box>
    </Dialog>
  );
}

export { CaissesDialog, caisseLabel };

export const caissesParamsFormActionCreator = () => (
  alertRoutine.show({
    type: 'full_dialog',
    neverHide: true,
    content: CaissesDialog,
  })
);
